import AddIcon from '@mui/icons-material/Add';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TopicTag from 'components/UI/TopicTag';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useRef, useState } from 'react';
import { useUpdateEditableAttributesMutation } from 'services/api/clients.api';
import { type BusinessUnit, TOPIC_COLORS, type TopicConfig } from 'services/types/businessUnit';
import type { Client } from 'services/types/client';

interface Props {
  client: Client;
  businessUnit?: BusinessUnit;
}

const FALLBACK_COLOR = TOPIC_COLORS[7]; // gray

type AutocompleteOption = TopicConfig | { name: string; inputValue: string };

const filter = createFilterOptions<AutocompleteOption>();

const ClientTopicsEditor: React.FC<Props> = ({ client, businessUnit }) => {
  const [updateClient, { isLoading: isUpdating }] = useUpdateEditableAttributesMutation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showFreeInput, setShowFreeInput] = useState(false);
  const addBtnRef = useRef<HTMLDivElement>(null);
  const { notifySaved } = useFeedback();

  const config = businessUnit?.conversationTaggingConfig;
  const configuredTopics = config?.topics ?? [];
  const allowFreeForm = config?.allowFreeFormTopics ?? false;
  const currentTopics: string[] = client.topics ?? [];

  const addableTopics: TopicConfig[] = configuredTopics
    .filter((t) => t.isActive && !currentTopics.includes(t.name))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));

  const canAdd = addableTopics.length > 0 || allowFreeForm;

  const handleRemove = (topicName: string) => {
    if (isUpdating) return;
    const updated = currentTopics.filter((t) => t !== topicName);
    updateClient({ id: client.id, body: { topics: updated } })
      .unwrap()
      .then(() => notifySaved('Tag eliminado'))
      .catch(console.error);
  };

  const handleAdd = (topicName: string) => {
    if (isUpdating) return;
    const trimmed = topicName.trim().slice(0, 30);
    if (!trimmed || currentTopics.includes(trimmed)) return;
    updateClient({ id: client.id, body: { topics: [...currentTopics, trimmed] } })
      .unwrap()
      .then(() => notifySaved('Tag agregado'))
      .catch(console.error);
    setMenuAnchor(null);
    setShowFreeInput(false);
  };

  const handleClickAdd = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isUpdating) return;
    if (allowFreeForm && addableTopics.length === 0) {
      setShowFreeInput(true);
    } else {
      setMenuAnchor(e.currentTarget);
    }
  };

  if (currentTopics.length === 0 && !canAdd) {
    return (
      <Grid size={12}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          Sin tags
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid size={12}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {currentTopics.map((topicName) => {
          const cfg = configuredTopics.find((t) => t.name === topicName);
          return (
            <TopicTag
              key={topicName}
              topicName={topicName}
              color={cfg?.color ?? FALLBACK_COLOR}
              inactive={cfg ? !cfg.isActive : false}
              onDelete={() => handleRemove(topicName)}
            />
          );
        })}

        {isUpdating && <CircularProgress size={14} thickness={5} sx={{ ml: 0.5, color: 'text.secondary' }} />}

        {canAdd &&
          (showFreeInput ? (
            <Autocomplete<AutocompleteOption, false, false, boolean>
              options={addableTopics}
              freeSolo
              size="small"
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                if (inputValue.trim() && !options.some((o) => 'name' in o && o.name === inputValue)) {
                  filtered.push({ name: inputValue.slice(0, 25), inputValue });
                }
                return filtered;
              }}
              onChange={(_, val) => {
                if (!val) return;
                handleAdd(typeof val === 'string' ? val : val.name);
              }}
              onBlur={() => setShowFreeInput(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Nuevo tag..."
                  autoFocus
                  sx={{ width: 140 }}
                  slotProps={{
                    ...params.slotProps,
                    htmlInput: { ...params.slotProps.htmlInput, maxLength: 20 },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={'name' in option ? option.name : String(option)}>
                  {'inputValue' in option ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      + Agregar &quot;{option.name}&quot;
                    </Typography>
                  ) : (
                    <TopicTag
                      topicName={option.name}
                      color={'color' in option ? option.color : FALLBACK_COLOR}
                      size="small"
                    />
                  )}
                </Box>
              )}
              openOnFocus
            />
          ) : (
            <Box
              ref={addBtnRef}
              onClick={handleClickAdd}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              {currentTopics.length === 0 && <Typography variant="caption">Agregar tag</Typography>}
            </Box>
          ))}

        {/* Floating menu — doesn't expand the row */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {addableTopics.map((t) => (
            <MenuItem key={t.name} onClick={() => handleAdd(t.name)} dense>
              <TopicTag topicName={t.name} color={t.color} size="small" />
            </MenuItem>
          ))}
          {allowFreeForm && (
            <MenuItem
              dense
              onClick={() => {
                setMenuAnchor(null);
                setShowFreeInput(true);
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                + Tag personalizado...
              </Typography>
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Grid>
  );
};

export default ClientTopicsEditor;
